import type { FC } from "react";
import { Legends } from "app/components/React/legend/vertical-legends-with-search/VerticalLegendsWithSearch";
import { translate } from "app/filters/i18nFilter";
import { FadeWrapper } from "app/UtilitiesAndConstants/UtilitiesComponents/FadeWrapper";

import { LegendWithOneLineBulletFlex, LegendWithOneLineCheckboxFlex } from "@similarweb/ui-components";
import type { ITransformedDataPoint } from "app/common/keyword-research/clicks-over-time-graph/graphDataUtils";
import { MAX_NUMBER_OF_DOMAINS } from "app/common/keyword-research/clicks-over-time-graph/graphDataUtils";
import { abbrNumberFilter, minAbbrNumberFilter } from "app/filters/ngFilters";
import { EGraphGranularities } from "app/common/keyword-research/serp-players/consts";

import {
    LegendItemsContainerWrapper,
    TableHeaderContainer,
    TableHeaderText,
    TotalClicksLegendContainer,
} from "app/components/keyword-research/clicks-over-time-graph/styledComponents";
import { THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE } from "app/filters/const";

import type { IGraphTitles } from "app/common/keyword-research/clicks-over-time-graph/graphDataUtils";

const granularityToI18nKey = {
    [EGraphGranularities.MONTHLY]: "keywordanalysis.organic2.clicks.over.time.monthly.average",
    [EGraphGranularities.WEEKLY]: "keywordanalysis.organic2.clicks.over.time.weekly.average",
    [EGraphGranularities.DAILY]: "keywordanalysis.organic2.clicks.over.time.daily.average",
};

export interface LegendDataPoint {
    name: string;
    color: string;
    data: string;
    visible: boolean;
    isTotal?: boolean;
    isOther?: boolean;
}

export type ExtraFiledType = keyof Pick<LegendDataPoint, "isOther" | "isTotal">;

interface ChartLabelsProps {
    selectedGranularity: EGraphGranularities;
    sites: ITransformedDataPoint[];
    extraFieldType?: ExtraFiledType;
    onSiteToggle: (series?: LegendDataPoint) => void;
    displayTooltipOnLegendOver?: boolean;
    graphTitles: IGraphTitles;
}

export const ChartLabels: FC<ChartLabelsProps> = ({
    selectedGranularity,
    sites,
    extraFieldType,
    onSiteToggle,
    displayTooltipOnLegendOver = false,
    graphTitles,
}) => {
    const sitesWithoutExtra = sites.filter((site) => !site?.isTotal && !site?.isOther);
    const extraField = sites.find((site) => site[extraFieldType]);
    const numberOfActiveSites = sitesWithoutExtra.reduce((agr, cur) => {
        if (cur.visible) {
            agr++;
        }
        return agr;
    }, 0);

    const isSelectable = !!extraFieldType;
    const TotalLegendComponent = isSelectable ? LegendWithOneLineCheckboxFlex : LegendWithOneLineBulletFlex;
    return (
        <>
            <TableHeaderContainer>
                <TableHeaderText>{translate(graphTitles.labelTitle)}</TableHeaderText>
                <TableHeaderText>{translate(granularityToI18nKey[selectedGranularity])}</TableHeaderText>
            </TableHeaderContainer>

            <LegendItemsContainerWrapper height={extraFieldType != null ? "240px" : "260px"}>
                <FadeWrapper>
                    <Legends
                        legendItems={sitesWithoutExtra.map((site) => {
                            const isDisabled = numberOfActiveSites >= MAX_NUMBER_OF_DOMAINS && !site.visible;
                            const tooltipText = displayTooltipOnLegendOver && site.name;
                            return {
                                ...site,
                                data:
                                    site.averageData === 0
                                        ? `<${THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE}`
                                        : minAbbrNumberFilter()(site.averageData, THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE),
                                // disable legend if user selected the max number of domains
                                isDisabled,
                                ...(isDisabled && {
                                    tooltip: translate("checkbox.max.selection"),
                                }),
                                tooltip: isDisabled ? translate("checkbox.max.selection") : tooltipText,
                            };
                        })}
                        // @ts-ignore
                        toggleSeries={(series) => onSiteToggle(series)}
                    />
                </FadeWrapper>
            </LegendItemsContainerWrapper>
            {extraFieldType != null ? (
                <TotalClicksLegendContainer>
                    <TotalLegendComponent
                        text={extraField?.name}
                        value={abbrNumberFilter()(extraField?.averageData)}
                        isChecked={extraField?.visible}
                        isDisabled={!isSelectable}
                        labelColor={isSelectable ? extraField?.color : "transparent"}
                        onClick={() => onSiteToggle({ name: extraField?.name } as LegendDataPoint)}
                    />
                </TotalClicksLegendContainer>
            ) : null}
        </>
    );
};
